import { ActionTypes } from '../actions/details_index';

const initialState = {};

// Single source of truth for the per-scorecard entry shape and its defaults,
// keyed state[eventId][detailsKey] (detailsKey = the row's member ids, see
// utils/details_utils.js). Every case that writes an entry must spread this
// (via getDetailsState) so a partial entry can never be created.
function createDetailsState() {
  return {
    loading: false,
    error: null,
    data: {},
  };
}

function getDetailsState(state, eventId, detailsKey) {
  return state[eventId]?.[detailsKey] ?? createDetailsState();
}

export default function detailsReducer(state = initialState, action) {
  switch (action.type) {
    case ActionTypes.FETCH_DETAILS_REQUEST: {
      const { eventId, detailsKey } = action.payload;
      const prev = getDetailsState(state, eventId, detailsKey);

      return {
        ...state,
        [eventId]: {
          ...state[eventId],
          [detailsKey]: { ...prev, loading: true, error: null },
        },
      };
    }

    case ActionTypes.FETCH_DETAILS_SUCCESS: {
      const { eventId, detailsKey, data } = action.payload;
      const prev = getDetailsState(state, eventId, detailsKey);

      return {
        ...state,
        [eventId]: {
          ...state[eventId],
          [detailsKey]: { ...prev, loading: false, data },
        },
      };
    }

    case ActionTypes.FETCH_DETAILS_FAILURE: {
      const { eventId, detailsKey, error } = action.payload;
      const prev = getDetailsState(state, eventId, detailsKey);

      return {
        ...state,
        [eventId]: {
          ...state[eventId],
          [detailsKey]: { ...prev, loading: false, error },
        },
      };
    }

    case ActionTypes.REMOVE_DETAILS: {
      const { eventId, detailsKey } = action.payload;
      const event = state[eventId];
      if (!event || !(detailsKey in event)) return state;

      const { [detailsKey]: _removed, ...rest } = event;

      // If that was the last scorecard for this event, drop the event entirely
      if (Object.keys(rest).length === 0) {
        const { [eventId]: _removedEvent, ...restState } = state;
        return restState;
      }

      return { ...state, [eventId]: rest };
    }

    case ActionTypes.REFRESH_DETAILS_BATCH_SUCCESS: {
      const { eventId, updates } = action.payload;
      const eventDetails = state[eventId];

      // If the event was closed before the response arrived, nothing to update
      if (!eventDetails) return state;

      const updated = Object.entries(updates).reduce((acc, [detailsKey, data]) => {
        const existing = eventDetails[detailsKey];
        // Skip if the scorecard was closed while the request was in-flight,
        // or if a user-initiated fetch is currently loading it (avoid clobbering)
        if (!existing || existing.loading) return acc;

        acc[detailsKey] = { ...existing, data };
        return acc;
      }, {});

      if (Object.keys(updated).length === 0) return state;

      return {
        ...state,
        [eventId]: { ...eventDetails, ...updated },
      };
    }

    case ActionTypes.REMOVE_ALL_DETAILS: {
      const { eventId } = action.payload;
      const { [eventId]: _removedEvent, ...restState } = state;
      return restState;
    }

    default:
      return state;
  }
}
